(() => {
  if (window.AraSectionMotionV6Loaded) return;
  window.AraSectionMotionV6Loaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sectionSelector = '.shopify-section';
  const excludedSectionSelector = [
    '.header-section',
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group',
    '[id*="shopify-section-header"]',
    '[id*="shopify-section-footer"]',
    '.cart-drawer-section',
    '[id*="cart-drawer"]',
    '.ara-menu-navigation-section',
    '.ara-menu-collection-section'
  ].join(',');

  const excludedContentSelector = [
    'dialog',
    '.menu-drawer',
    '.cart-drawer',
    '.predictive-search',
    '.zoom-dialog',
    '.ara-product-order__thumbs',
    '.ara-product-order__zoom-dialog',
    '[hidden]',
    '[aria-hidden="true"]'
  ].join(',');

  const copySelector = [
    '.ara-eyebrow',
    '.ara-heading-xl',
    '.ara-heading-lg',
    '.ara-heading-md',
    '.ara-subheading',
    '.ara-body',
    '.ara-body-small',
    '.section__title',
    '.section-heading',
    'h1',
    'h2',
    'h3',
    '.rte',
    '.button',
    '.ara-button-text'
  ].join(',');

  const cardSelector = [
    '.product-card',
    '.collection-card',
    '[class$="__card"]',
    '[class$="__item"]',
    '[class*="__card "]',
    '[class*="__item "]'
  ].join(',');

  const mediaSelector = [
    'picture',
    '.media',
    '[class$="__media"]',
    '[class$="__thumb"]',
    '[class$="__image-wrap"]',
    '[class$="__image-wrapper"]'
  ].join(',');

  const lineSelector = [
    '.ara-joy-intro__line',
    '.ara-home-craft__rule',
    '.ara-home-favorites__link'
  ].join(',');

  let observer = null;

  const isUsable = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest(excludedContentSelector)) return false;
    if (element.closest('header, footer, nav')) return false;

    const style = window.getComputedStyle(element);

    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return element.getClientRects().length > 0;
  };

  const isMotionSafe = (element) => {
    if (!(element instanceof HTMLElement)) return false;

    const style = window.getComputedStyle(element);

    return style.transform === 'none' && style.position !== 'fixed' && style.position !== 'sticky';
  };

  const getStage = (section) => {
    return Array.from(section.children).find((child) => {
      if (!(child instanceof HTMLElement)) return false;
      if (['STYLE', 'SCRIPT', 'TEMPLATE', 'LINK'].includes(child.tagName)) return false;
      return isUsable(child);
    }) || null;
  };

  const uniqueTopLevel = (elements, limit) => {
    const selected = [];

    elements.forEach((element) => {
      if (selected.length >= limit || !isUsable(element) || !isMotionSafe(element)) return;
      if (selected.some((item) => item.contains(element))) return;
      selected.push(element);
    });

    return selected;
  };

  const assignDelay = (element, delay) => {
    element.style.setProperty('--ara-motion-delay', `${delay}ms`);
  };

  const revealSection = (section, immediate = false) => {
    if (!(section instanceof HTMLElement)) return;
    if (section.dataset.araMotionPlayed === 'true') return;

    section.dataset.araMotionPlayed = 'true';
    observer?.unobserve(section);

    const finish = () => {
      section.classList.add('ara-motion-visible');

      window.setTimeout(() => {
        section.classList.remove('ara-motion-pending');
      }, 1450);
    };

    if (immediate || reducedMotion.matches) {
      finish();
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(finish);
    });
  };

  const prepareSection = (section) => {
    if (!(section instanceof HTMLElement)) return;
    if (section.matches(excludedSectionSelector)) return;
    if (section.dataset.araMotionReady === 'true') return;

    const stage = getStage(section);
    if (!stage) return;

    section.dataset.araMotionReady = 'true';
    stage.classList.add('ara-motion-stage');

    const cards = uniqueTopLevel(
      Array.from(stage.querySelectorAll(cardSelector)),
      12
    );

    cards.forEach((element, index) => {
      element.classList.add('ara-motion-card');
      assignDelay(element, 70 + Math.min(index, 9) * 65);
    });

    const copy = uniqueTopLevel(
      Array.from(stage.querySelectorAll(copySelector)).filter((element) => {
        return !element.closest('.ara-motion-card');
      }),
      10
    );

    copy.forEach((element, index) => {
      element.classList.add('ara-motion-copy');
      assignDelay(element, 30 + Math.min(index, 8) * 55);
    });

    const media = [];

    Array.from(stage.querySelectorAll('img')).forEach((image) => {
      if (media.length >= 5 || !isUsable(image)) return;
      if (image.classList.contains('header-logo__image')) return;
      if (image.naturalWidth && image.naturalWidth < 220) return;

      const frame = image.closest(mediaSelector) || image;

      if (!(frame instanceof HTMLElement) || !isMotionSafe(frame)) return;
      if (frame.closest('.ara-motion-card')) return;
      if (media.includes(frame)) return;

      frame.classList.add('ara-motion-media');
      assignDelay(frame, 60 + media.length * 85);
      media.push(frame);

      const hoverFrame = frame.closest(
        'a, .product-card, .collection-card, [class$="__card"]'
      ) || frame;

      if (hoverFrame instanceof HTMLElement) {
        hoverFrame.classList.add('ara-motion-hover');
      }
    });

    Array.from(stage.querySelectorAll(lineSelector)).forEach((line, index) => {
      if (!isUsable(line) || !isMotionSafe(line)) return;
      line.classList.add('ara-motion-line');
      assignDelay(line, 110 + index * 70);
    });

    const rect = section.getBoundingClientRect();
    const alreadyVisible = rect.bottom <= 0 || rect.top < window.innerHeight * 0.86;

    if (!observer || reducedMotion.matches || alreadyVisible) {
      revealSection(section, true);
      return;
    }

    section.classList.add('ara-motion-pending');
    observer.observe(section);
  };

  const initialize = (scope = document) => {
    const sections = scope.matches?.(sectionSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(sectionSelector) || []);

    sections.forEach(prepareSection);
  };

  if ('IntersectionObserver' in window && !reducedMotion.matches) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealSection(entry.target);
      });
    }, {
      rootMargin: '0px 0px 10% 0px',
      threshold: 0.01
    });
  }

  const start = () => initialize(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    initialize(document);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.closest?.(sectionSelector);

    if (!section) return;

    prepareSection(section);
    revealSection(section, true);
  });
})();
